import React, {forwardRef, useEffect, useRef} from 'react';
import {Tab as MuiTab, Tabs as MuiTabs} from '@mui/material';
import {makeStyles} from "@mui/styles";
import classnames from "classnames";
import useIsMounted from "../../../hooks/use-is-mounted";
import useCombinedRefs from "../../../hooks/use-combined-refs";

const useStyles = makeStyles(() => ({
    root: {
        minWidth: props => props.minWidth,
    },
}))

/**
 * @type {React.ForwardRefExoticComponent<React.PropsWithChildren<React.PropsWithoutRef<import('@mui/material').TabsProps & {
 * readonly action?: *,
 * }>> & React.RefAttributes<HTMLButtonElement>>}
 */
export const Tabs = forwardRef(({
                                    children,
                                    action,
                                    ...props
                                }, ref) => {
    /**@type {React.MutableRefObject<import('@mui/material').TabsActions>}*/
    const actionsRef = useRef();
    const combinedRef = useCombinedRefs(actionsRef, action);
    const initialRender = useRef(true);
    const isMounted = useIsMounted();

    /**
     * With each re-render of the component:
     * - updates the indicators position
     * - if in initial render, sets a timer for 500 ms prior to updating the indicator
     */
    useEffect(() => {
        if (!actionsRef.current)
            return;
        actionsRef.current.updateIndicator();

        if (!initialRender.current)
            return;
        initialRender.current = false;

        setTimeout(() => {
            if (!isMounted())
                return;
            actionsRef.current?.updateIndicator();
        }, 500)
    })

    return (
        <MuiTabs
            ref={ref}
            action={combinedRef}
            children={children}
            variant={"scrollable"}
            scrollButtons={"auto"}
            allowScrollButtonsMobile
            {...props}
            value={props?.value ?? false}
            TabScrollButtonProps={{
                ...(props?.TabScrollButtonProps ?? {}),
                disableRipple: true,
            }}
        />
    );
})

/**
 *
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{
 * readonly selectedFontSize?: number,
 * readonly fontSize?: number,
 * readonly minWidth?: string | number,
 * }> & Partial<import('@mui/material').TabProps> & React.RefAttributes<HTMLDivElement>>}
 */
export const Tab = forwardRef(({
                                   minWidth = 72,
                                   classes: classesProps,
                                   ...props
                               }, ref) => {
    const classes = useStyles({minWidth});

    return (
        <>
            <MuiTab
                ref={ref}
                classes={{
                    ...classesProps,
                    selected: classes.selected,
                    root: classnames(classes.root, classesProps?.root)
                }}
                disableRipple
                {...props}
            />
        </>

    );
});
